import * as React from 'react';
import { ProductSelection, LocationDateSelection, Confirm, Summary, ViewProp } from './';
import { className } from '../util';
import { ArticleType } from '../mrCapTypes';
import { LocationSelection } from './location/viewLocation';

type ValidatorFunc = (prop: ViewProp) => boolean;

interface MoveResult {
  ok: boolean;
  error?: string;
}

interface Rule {
  func: ValidatorFunc;
  errorMessage: string;
}

const rule = (func: ValidatorFunc, error: string) =>
  ({ func, errorMessage: error } as Rule);

function makeRules(rules: Rule[]) {
  return (prop: ViewProp) => {
    const error = rules.find(r => !r.func(prop));

    return {
      ok: !error,
      error: error?.errorMessage
    } as MoveResult;
  };
}

enum StepType {
  location,
  productSelection,
  locationAndDate,
  payment,
  summary
}

interface ViewStepObj {
  id: string;
  name: string;
  nextButton: string;
  prevButton?: string;
  component: React.FC<ViewProp> | React.ComponentClass<ViewProp>;
  canMoveNext: (prop: ViewProp) => MoveResult;
}

export const views: ViewStepObj[] = [
  {
    id: 'location',
    name: 'Välj anläggning',
    nextButton: 'Fortsätt',
    component: LocationSelection,
    canMoveNext: makeRules([
      rule(prop => prop.selectedShop != null, 'Du måste välja en anläggning för att fortsätta')
    ])
  },
  {
    id: 'treatment',
    name: 'Välj behandling',
    prevButton: 'Tillbaka',
    nextButton: 'Fortsätt',
    component: ProductSelection,
    canMoveNext: makeRules([
      rule(
        prop => prop.selectedProducts.length > 0,
        'Välj någon <strong>produkt</strong> för att fortsätta.'
      ),
      rule(
        prop => prop.selectedProducts.every(p => p.type === ArticleType.product) ||
        (prop.regNr !== '' && !!prop.carDetails),
        'Fyll i ditt <strong>registreringsnummer</strong> och hämta fordonsinformationen för att fortsätta.'
      ),
      rule(
        prop => prop.selectedProducts.every(p => p.type === ArticleType.product) ||
        (prop.regNr !== '' && !!prop.carDetails && prop.carDetails.weight < prop.weightThreshold),
        'Fordonet du vill boka in väger för mycket. Vänligen ring oss för att boka din behandling.'
      )
    ])
  },
  {
    id: 'date',
    name: 'Välj datum',
    prevButton: 'Tillbaka',
    nextButton: 'Fortsätt',
    component: LocationDateSelection,
    canMoveNext: makeRules([
      rule(prop => prop.selectedShop != null && prop.selectedDate != null,
        'Välj en <strong>anläggning</strong> och ett <strong>datum</strong> för att fortsätta.')
    ])
  },
  {
    id: 'payment',
    name: 'Bekräfta och betala',
    nextButton: 'Betala',
    prevButton: 'Tillbaka',
    component: Confirm,
    canMoveNext: makeRules([
      rule(prop => true,
        'Ange <strong>namn</strong> och <strong>e-post</strong> för att forstsätta.')
      // This gets validated in other ways in confirm.tsx
    ])
  },
  {
    id: 'summary',
    name: 'Sammanfattning',
    nextButton: 'Till startsidan',
    component: Summary,
    canMoveNext: props => ({ ok: true })
  }
];

export function ViewSteps({ viewIndex }: { viewIndex: number }) {
  const finished = viewIndex === 4;

  return (
    <div className={className('view-steps', {finished})}>
      {views.map((view, index) =>
        <ViewStep
          key={view.id}
          name={view.name}
          num={index + 1}
          selected={index === viewIndex}
          completed={viewIndex > index}
          finished={finished} />)}
    </div>
  );
}

interface ViewStepProps {
  name: string;
  num: number;
  selected: boolean;
  completed: boolean;
  finished: boolean;
}

function ViewStep({ name, num, selected, completed, finished }: ViewStepProps) {

  return (
    <div className={className('view-step', { selected, completed })}>
      <h4 className='view-step-title'>{name}</h4>

      <div className='view-step-status'>
        <div className={className('view-step-num', {completed})}>
          {completed || finished ? <i className='fas fa-check'></i> : <span>{num}</span>}
        </div>
        <div className={className('view-step-line', {completed})}></div>
      </div>
    </div>
  );
}

interface ViewNavProps {
  viewIndex: number;
  view: ViewStepObj;
  change: (index: number) => void;
  financing: boolean;
  onlyProducts: boolean;
  setFinancing: (value: boolean) => void;
}

export function ViewNav(props: ViewNavProps) {

  const changeStep = (modifier: number) => {
    return (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();

      if (!props.financing && props.onlyProducts) {
        props.setFinancing(true);
      }

      props.change(Math.min(Math.max(0, props.viewIndex + modifier), views.length - 1));
    };
  };

  /*
    The step changing below might be confusing.

    - If the visitor is currently on step 2 (StepType.productSelection), and only
    selects products, not treatments or other services that requires booking,
    they do not need to book any shop and date. Therefore, skip that step.

    - Same logic applies if the visitor wants to back.
    If the visitor is currently on step 4 (StepType.payment), has only
    selected products and not treatments, the visistor should end up on
    step 2 (StepType.productSelection), since he/she hasn't booked any timeslot.

    - On step 4 (StepType.payment), the next or forward button is hidden since
    components on this step uses other buttons to trigger step changing.

    - For the last step (5 / StepType.summary), we want to reload the page
    instead of backing or going forward to a non-existing step.
  */

  return (
    <div className='view-nav'>
      {props.view.prevButton && props.viewIndex !== StepType.summary &&
        <div className='cta-btn left-btn'
          onClick={changeStep(
            props.onlyProducts && props.viewIndex === StepType.payment
              ? -2
              : -1
          )}>
          {props.view.prevButton}
        </div>}

      {props.view.nextButton && props.viewIndex !== StepType.payment &&
        <div className='cta-btn primary right-btn'
          onClick={
            props.viewIndex === StepType.summary
              ? () => location.reload()
              : changeStep(props.onlyProducts && props.viewIndex === StepType.productSelection ? 2 : 1 )}>
          {props.view.nextButton}
        </div>}
    </div>
  );
}
